/**
 * Dashboard Page Component
 *
 * Shows user stats, plan info, and recent activity.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listJobs } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';

function DashboardPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [recentJobs, setRecentJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalCountries, setTotalCountries] = useState(0);
  const [loading, setLoading] = useState(true);

  // Build user object from profile
  const user = profile ? {
    name: profile.name || (profile.email ? profile.email.split('@')[0] : 'User'),
    email: profile.email || '',
    plan: profile.plan || 'free',
    leads_used_today: profile.leads_used_today || 0,
  } : null;

  // Plan limits config
  const planLimits = { free: 0, basic: 50, standard: 500, premium: 999999, trial: 1000 };
  const dailyLimit = planLimits[user?.plan] || 0;
  const dailyUsed = user?.leads_used_today || 0;
  const usagePercent = dailyLimit > 0 && dailyLimit < 999999 ? Math.round((dailyUsed / dailyLimit) * 100) : (dailyLimit >= 999999 ? 0 : 0);

  const planConfig = {
    free: { name: 'Free Plan', color: 'text-red-400', bg: 'bg-red-500/10', border: '', leadsLabel: 'No leads — Upgrade required' },
    basic: { name: 'Basic Plan', color: 'text-gray-400', bg: 'bg-gray-500/10', border: '', leadsLabel: '50 leads/day' },
    standard: { name: 'Standard Plan', color: 'text-blue-400', bg: 'bg-blue-500/10', border: '', leadsLabel: '500 leads/day' },
    premium: { name: 'Premium Plan', color: 'text-purple-400', bg: 'bg-purple-500/10', border: '', leadsLabel: 'Unlimited leads' },
    trial: { name: 'Free Trial', color: 'text-purple-400', bg: 'bg-purple-500/20', border: '', leadsLabel: '1000 leads/day' },
  };

  const currentPlanConfig = planConfig[user?.plan] || planConfig.free;

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await listJobs({ limit: 5 });
      const activeJobs = data.jobs || [];
      setRecentJobs(activeJobs);
      setTotalJobs(data.total || 0);

      const uniqueCountries = new Set(activeJobs.map(j => j.country).filter(Boolean));
      setTotalCountries(uniqueCountries.size);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-zinc-950 min-h-screen">
      <Helmet>
        <title>Dashboard - LeadGen AI</title>
        <meta name="description" content="Your LeadGen AI dashboard. Search for business leads, view usage stats and manage your account." />
      </Helmet>
      {/* Welcome Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate('/search')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 text-sm"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-bold text-white">
          Welcome back, <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{user?.name || 'User'}</span> 👋
        </h1>
        <p className="text-gray-400 mt-1">Your lead generation overview</p>
      </div>

      {/* Trial Banner */}
      {user?.plan === 'trial' && (
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-purple-900/40 to-pink-900/40 ">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-lg font-bold text-white mb-0.5">🚀 Free Premium Trial Active!</div>
              <div className="text-sm text-gray-400">You have full Premium access. Upgrade before trial ends!</div>
            </div>
            <Link to="/pricing" className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors whitespace-nowrap">
              View Plans →
            </Link>
          </div>
        </div>
      )}

      {/* Free Plan Banner */}
      {user?.plan === 'free' && (
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-red-900/40 to-orange-900/40">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-lg font-bold text-white mb-0.5">⚠️ Your Trial Has Ended</div>
              <div className="text-sm text-gray-400">Upgrade to a paid plan to continue finding leads and grow your business.</div>
            </div>
            <Link to="/pricing" className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors whitespace-nowrap">
              Upgrade Now →
            </Link>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Plan Card */}
        <div className={`rounded-2xl ${currentPlanConfig.bg} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <span className="text-xl">💎</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${currentPlanConfig.bg} ${currentPlanConfig.color}`}>
              {user?.plan === "trial" ? "🚀 Free Trial" : user?.plan === "free" ? "⚠️ Free" : user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : 'Free'}
            </span>
          </div>
          <h3 className="text-gray-400 text-sm">Current Plan</h3>
          <p className="text-white text-xl font-bold mt-1">{currentPlanConfig.name}</p>
          <p className="text-gray-500 text-sm mt-1">{currentPlanConfig.leadsLabel}</p>
        </div>

        {/* Daily Usage Card */}
        <div className="rounded-2xl bg-zinc-900 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <span className="text-xl">📊</span>
            </div>
          </div>
          <h3 className="text-gray-400 text-sm">Daily Usage</h3>
          <p className="text-white text-xl font-bold mt-1">
            {dailyUsed} <span className="text-base text-gray-500 font-normal">/ {dailyLimit >= 999999 ? '∞' : dailyLimit}</span>
          </p>
          <div className="w-full bg-zinc-800 rounded-full h-2 mt-3">
            <div
              className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            ></div>
          </div>
          <p className="text-gray-500 text-sm mt-2">{usagePercent}% used today</p>
        </div>

        {/* Total Searches Card */}
        <div className="rounded-2xl bg-zinc-900 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <span className="text-xl">🔍</span>
            </div>
          </div>
          <h3 className="text-gray-400 text-sm">Total Searches</h3>
          <p className="text-white text-xl font-bold mt-1">{totalJobs}</p>
          <p className="text-gray-500 text-sm mt-1">All time searches</p>
        </div>
      </div>

      {/* Freelancer Profile Card */}
      <div className="bg-zinc-900 rounded-2xl p-6 mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-2xl">🚀</span>
          <div>
            <h3 className="text-white font-semibold">Freelancer Profile</h3>
            <p className="text-gray-400 text-sm">Update your skills, pricing, and target clients</p>
          </div>
        </div>
        <Link to="/freelancer-setup" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors">
          Edit Profile
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="rounded-2xl bg-zinc-900 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Recent Searches</h2>
          <Link to="/history" className="text-blue-400 hover:text-blue-300 text-sm font-medium">
            View All →
          </Link>
        </div>

        {recentJobs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No searches yet. Start your first search!</p>
            <Link to="/search" className="inline-block mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition">
              Search Now
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentJobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-4 rounded-xl bg-zinc-800">
                <div>
                  <p className="text-white font-medium">{job.business_type}</p>
                  <p className="text-gray-500 text-sm">{job.location}, {job.country}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {job.status}
                  </span>
                  <p className="text-gray-500 text-sm mt-1">{job.total_leads || 0} leads</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Link to="/search" className="flex items-center space-x-3 p-4 rounded-2xl bg-zinc-900 hover:bg-zinc-800 transition-all duration-200 group">
          <span className="text-2xl">🔍</span>
          <div>
            <p className="text-white font-medium group-hover:text-blue-400 transition">New Search</p>
            <p className="text-gray-500 text-sm">Find new leads</p>
          </div>
        </Link>
        <Link to="/history" className="flex items-center space-x-3 p-4 rounded-2xl bg-zinc-900 hover:bg-zinc-800 transition-all duration-200 group">
          <span className="text-2xl">📋</span>
          <div>
            <p className="text-white font-medium group-hover:text-purple-400 transition">Search History</p>
            <p className="text-gray-500 text-sm">View past searches</p>
          </div>
        </Link>
        <Link to="/pricing" className="flex items-center space-x-3 p-4 rounded-2xl bg-zinc-900 hover:bg-zinc-800 transition-all duration-200 group">
          <span className="text-2xl">⚡</span>
          <div>
            <p className="text-white font-medium group-hover:text-green-400 transition">Upgrade Plan</p>
            <p className="text-gray-500 text-sm">Get more leads</p>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default DashboardPage;
